import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { OrderPartyDetail, OrderPartyTypeEnum } from 'src/app/inventory/interfaces/order-party-details.interface';
import { deepCopy } from 'src/app/shared/helpers/helpers';
import { IAutocompleteDataRow } from 'src/app/shared/interfaces/autocomplete-data-row.interface';
import { IThreeDotsMenuOption } from 'src/app/shared/interfaces/three-dots-menu-option.interface';
import { SaveSupplierBuyer } from 'src/app/shared/states/orders/orders.actions';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';

@Component({
  selector: 'app-supplier-buyer-card',
  templateUrl: './supplier-buyer-card.component.html',
  styleUrls: ['./supplier-buyer-card.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate('200ms', style({opacity: 1})),
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class SupplierBuyerCardComponent implements OnInit {
  @Input() data: OrderPartyDetail;
  @Input() type: OrderPartyTypeEnum = OrderPartyTypeEnum.Supplier;
  @Input() readOnly: boolean;
  @Input() autocompleteData: IAutocompleteDataRow[] = [];
  @Input() isPharmacy: boolean; //if the order party is the pharmacy itself
  @Output() selected: EventEmitter<IAutocompleteDataRow> = new EventEmitter();
  @Output() partyRemoved: EventEmitter<void> = new EventEmitter();

  @Output() addedModified: EventEmitter<OrderPartyDetail> = new EventEmitter();

  @Select(OrdersState.loading) loading$: Observable<boolean | undefined>;
  @Select(OrdersState.success) success$: Observable<boolean>;


  control = new FormControl('', [Validators.required]);
  addModifyDialog: boolean;

  OrderPartyTypeEnum = OrderPartyTypeEnum;

  menuOptions: IThreeDotsMenuOption[] = [];

  autocompleteLabel = '';
  autocompletePlaceholder = '';

  @ViewChild('autocomplete') autocomplete;
  /**
   *
   */
  constructor(private store: Store, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.menuOptions = [{
      id: MenuOptionID.edit,
      name: 'Edit',
      svgPath: '/assets/icons/icon-edit-pencil.svg',
      activeSvgPath: '/assets/icons/icon-edit-pencil-active.svg'
    }];
    if (this.type === OrderPartyTypeEnum.Buyer || this.type === OrderPartyTypeEnum.EdiSupplier ) {
      this.menuOptions.push({
        id: MenuOptionID.remove,
        name: 'Remove',
        svgPath: '/assets/icons/icon-basket.svg',
        activeSvgPath: '/assets/icons/icon-basket-active.svg'
      });
    }
    this.autocompleteLabel = this.type === OrderPartyTypeEnum.Buyer ? 'Buyer' : 'Supplier';
    this.autocompletePlaceholder = `Select ${this.type === OrderPartyTypeEnum.Buyer ? 'buyer' : 'supplier'} or add a new one`;
  }

  partySelected(event: IAutocompleteDataRow): void {
    this.selected.next(event);
  }

  menuItemSelected(option: IThreeDotsMenuOption) {
    switch (option.id) {
      case MenuOptionID.remove:
        this.control.patchValue('');
        this.partyRemoved.next();
        break;
      case MenuOptionID.edit:
        this.addModifyDialog = true;
        break;
      default:
        break;
    }
  }

  addBuyer(): void {
    this.data = {orderParty: {companyName: this.control.value}} as any;
    this.addModifyDialog = true; 
  }

  saveSubmitted(data: OrderPartyDetail): void {
      data.orderParty.id = this.data?.orderParty.id;
      data.orderPartyID = this.data?.orderPartyID;
      this.store.dispatch(new SaveSupplierBuyer(data))
      .pipe(untilDestroyed(this), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        if (success) {
          const copy = deepCopy(data);
          const selected = this.store.selectSnapshot(OrdersState.addedModifiedOrderParty);
          if (selected) {
            copy.id = selected.orderPartyDetailID;
            copy.orderPartyID = selected.orderPartyID;
            copy.orderParty.id = selected.orderPartyID;
          }
          this.addedModified.next(copy);
          this.addModifyDialog = false;
        }
      })
  }

  focus(): void {
    this.autocomplete.focus();
  }

  highlightRequiredControlsWithErrors(): void {
    if (this.control.hasError('required') && (this.control?.value === '' || this.control?.value === null)) {
      this.control.markAsTouched();
      this.control.markAsDirty();
      this.control.updateValueAndValidity();
      this.autocomplete?.markForCheck();
    }
  }
}

enum MenuOptionID  {
  edit,
  remove
}

